/**
 * The live Nextcloud card body (#98). Renders what the proxy returns: members
 * see the state, owners/admins the connection, the exposed folders and the
 * actions their rights allow (`canManage`, `canPick`). Every value from the
 * server goes in via textContent.
 */

import type {
  ExposedFolder,
  NextcloudStatus,
} from '../../../core/integrations-service';
import type { TranslationParams } from '../../../i18n/ui-manager.types';

type Translate = (key: string, params?: TranslationParams) => string;

export interface NextcloudCardView {
  status: NextcloudStatus | null;
  loading: boolean;
  /** shown in the message line (action or check failures) */
  message: string | null;
  /** the status could not be loaded at all */
  failed: boolean;
}

export interface NextcloudCardHandlers {
  connect(): void;
  reconnect(): void;
  confirm(): void;
  cancelPending(): void;
  addFolder(): void;
  removeFolder(folder: ExposedFolder): void;
  disconnect(): void;
  retry(): void;
}

const K = 'modules.integrations.nextcloud';

const BADGE_KEYS: Record<NextcloudStatus['state'], string> = {
  none: `${K}.state.notConnected`,
  pending: `${K}.state.pending`,
  active: `${K}.state.connected`,
  needsReconnect: `${K}.state.needsReconnect`,
};

/** The badge text key for the card's current view. */
export function nextcloudBadgeKey(view: NextcloudCardView): string {
  if (view.status) return BADGE_KEYS[view.status.state];
  return view.loading ? `${K}.state.loading` : `${K}.state.unknown`;
}

export function renderNextcloudCard(
  host: HTMLElement,
  view: NextcloudCardView,
  t: Translate,
  handlers: NextcloudCardHandlers
): void {
  host.replaceChildren();
  const { status } = view;

  if (!status) {
    if (view.loading) {
      host.append(text('p', t(`${K}.loading`), 'integrations__muted'));
    } else if (view.failed) {
      host.append(
        text('p', t(`${K}.errors.generic`), 'integrations__error'),
        actions([action('retry', t(`${K}.retry`), handlers.retry)])
      );
    }
    return;
  }

  if (!status.canManage) {
    const key =
      status.state === 'none'
        ? 'askAdmin'
        : status.state === 'needsReconnect'
          ? 'memberNeedsReconnect'
          : 'memberConnected';
    host.append(text('p', t(`${K}.${key}`), 'integrations__muted'));
    return;
  }

  switch (status.state) {
    case 'none':
      host.append(
        actions([
          action(
            'connect',
            t('modules.integrations.connect'),
            handlers.connect,
            true
          ),
        ])
      );
      break;
    case 'pending':
      host.append(
        details(status, t),
        text('p', t(`${K}.pendingHint`), 'integrations__muted'),
        actions([
          action('useAccount', t(`${K}.useAccount`), handlers.confirm, true),
          action('cancel', t(`${K}.cancel`), handlers.cancelPending),
        ])
      );
      break;
    case 'active':
      host.append(details(status, t), folders(status, t, handlers));
      if (!status.canPick) {
        host.append(
          text('p', t(`${K}.pickOnlyConnector`), 'integrations__muted')
        );
      }
      host.append(
        actions([
          ...(status.canPick
            ? [
                action(
                  'addFolder',
                  t(`${K}.addFolder`),
                  handlers.addFolder,
                  true
                ),
              ]
            : []),
          action('disconnect', t(`${K}.disconnect`), handlers.disconnect),
        ])
      );
      break;
    case 'needsReconnect':
      host.append(
        details(status, t),
        text('p', t(`${K}.reconnectHint`), 'integrations__warning'),
        actions([
          action('reconnect', t(`${K}.reconnect`), handlers.reconnect, true),
          action('disconnect', t(`${K}.disconnect`), handlers.disconnect),
        ])
      );
      break;
  }

  const message = text('p', view.message ?? '', 'integrations__error');
  message.dataset.nextcloudMessage = '';
  message.setAttribute('role', 'status');
  message.hidden = !view.message;
  host.append(message);
}

function details(status: NextcloudStatus, t: Translate): HTMLElement {
  const list = document.createElement('dl');
  list.className = 'integrations__details';
  const row = (label: string, value: string): void => {
    list.append(text('dt', label), text('dd', value));
  };
  if (status.server) row(t(`${K}.server`), status.server);
  if (status.account) {
    const { displayName, loginName } = status.account;
    row(
      t(`${K}.account`),
      displayName && displayName !== loginName
        ? `${displayName} (${loginName})`
        : loginName
    );
  }
  if (status.connectedBy && status.connectedAt) {
    row(
      t(`${K}.connectedByLabel`),
      `${status.connectedBy.name}, ${formatDate(status.connectedAt)}`
    );
  }
  return list;
}

function folders(
  status: NextcloudStatus,
  t: Translate,
  handlers: NextcloudCardHandlers
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'integrations__folders';
  section.append(text('h3', t(`${K}.folders`), 'integrations__subtitle'));
  const items = status.folders ?? [];
  if (items.length === 0) {
    section.append(text('p', t(`${K}.noFolders`), 'integrations__muted'));
    return section;
  }
  const list = document.createElement('ul');
  list.className = 'integrations__folder-list';
  for (const folder of items) {
    const item = document.createElement('li');
    item.className = 'integrations__folder';
    item.dataset.nextcloudFolder = folder.id;
    const label = document.createElement('div');
    label.className = 'integrations__folder-label';
    label.append(text('span', folder.name, 'integrations__folder-name'));
    if (folder.path !== folder.name) {
      label.append(text('span', folder.path, 'integrations__folder-path'));
    }
    if (!folder.available) {
      label.append(
        text('span', t(`${K}.unavailable`), 'integrations__warning')
      );
    }
    const remove = action('remove', t(`${K}.remove`), () =>
      handlers.removeFolder(folder)
    );
    remove.classList.add('btn-sm');
    remove.setAttribute('aria-label', `${t(`${K}.remove`)}: ${folder.name}`);
    item.append(label, remove);
    list.append(item);
  }
  section.append(list);
  return section;
}

function actions(buttons: HTMLButtonElement[]): HTMLElement {
  const row = document.createElement('div');
  row.className = 'integrations__actions';
  row.append(...buttons);
  return row;
}

function action(
  name: string,
  label: string,
  onClick: () => void,
  primary = false
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = primary ? 'btn btn-primary' : 'btn btn-outline-secondary';
  button.dataset.nextcloudAction = name;
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function text(tag: string, value: string, className?: string): HTMLElement {
  const element = document.createElement(tag);
  if (className) element.className = className;
  element.textContent = value;
  return element;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}
